<template>
  <div class="slideTextToolbar">
    <!-- TEXT COLOR -->
    <div class="toolbarColor">
      <w-color-picker
        :value="lastColor"
        @input="onColorChange"
        @active-change="onColorPickerActiveChange"
        position="bottom-right"
        size="small"
        representation="square"
        :empty-formater="true"
        custom-class="slideTextColorPicker"
      />
    </div>

    <div class="toolbarSeparator"></div>

    <!-- INLINE STYLES -->
    <button
      type="button"
      class="toolbarButton"
      :class="{ active: state.bold }"
      :title="$t('aiAvatar.textToolbar.bold')"
      @mousedown.prevent="exec('bold')"
    >
      <w-icon icon="format-bold" />
    </button>
    <button
      type="button"
      class="toolbarButton"
      :class="{ active: state.italic }"
      :title="$t('aiAvatar.textToolbar.italic')"
      @mousedown.prevent="exec('italic')"
    >
      <w-icon icon="format-italic" />
    </button>
    <button
      type="button"
      class="toolbarButton"
      :class="{ active: state.underline }"
      :title="$t('aiAvatar.textToolbar.underline')"
      @mousedown.prevent="exec('underline')"
    >
      <w-icon icon="format-underline" />
    </button>
    <button
      type="button"
      class="toolbarButton"
      :class="{ active: state.strikeThrough }"
      :title="$t('aiAvatar.textToolbar.strikethrough')"
      @mousedown.prevent="exec('strikeThrough')"
    >
      <w-icon icon="format-strikethrough-variant" />
    </button>

    <div class="toolbarSeparator"></div>

    <!-- LISTS -->
    <button
      type="button"
      class="toolbarButton"
      :class="{ active: state.insertUnorderedList }"
      :title="$t('aiAvatar.textToolbar.bulletList')"
      @mousedown.prevent="exec('insertUnorderedList')"
    >
      <w-icon icon="format-list-bulleted" />
    </button>
    <button
      type="button"
      class="toolbarButton"
      :class="{ active: state.insertOrderedList }"
      :title="$t('aiAvatar.textToolbar.numberedList')"
      @mousedown.prevent="exec('insertOrderedList')"
    >
      <w-icon icon="format-list-numbered" />
    </button>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import WIcon from "@/components/wrapper/w-icon.vue";
import WColorPicker from "@/components/wrapper/w-colorPicker.vue";
import { SlideTextCommand } from "@/components/recorder/aiAvatar/panel/AISlideConstant";

@Component({
  components: { WIcon, WColorPicker },
})
export default class SlideTextToolbar extends Vue {
  // Last applied color, used as the color picker swatch default.
  @Prop({ default: "#000000" })
  private lastColor!: string;

  // Active/toggled state of each command at the current caret (queryCommandState).
  @Prop({ default: () => ({}) })
  private state!: Record<string, boolean>;

  private exec(cmd: string) {
    this.$emit("command", { cmd } as SlideTextCommand);
  }

  private onColorChange(value: string) {
    this.$emit("command", { cmd: "foreColor", value } as SlideTextCommand);
  }

  // Tell the parent when the color picker dropdown opens/closes, so it can keep
  // the edited field "logically focused" and restore the caret once it closes.
  private onColorPickerActiveChange(active: boolean) {
    this.$emit("color-picker-active", active);
  }
}
</script>

<style lang="scss" scoped>
.slideTextToolbar {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0px 8px;
  background: white;
  border-radius: 20px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.18);

  .toolbarColor {
    display: flex;
    align-items: center;
    ::v-deep .slideTextColorPicker {
      .dropdown-trigger button,
      .button {
        border-radius: 12px;
        width: 32px;
        height: 32px;
        padding: 0;
      }
    }
  }

  .toolbarSeparator {
    width: 1px;
    height: 48px;
    margin: 0 4px;
    background: var(--light);
  }

  .toolbarButton {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    border-radius: 12px;
    cursor: pointer;
    color: var(--black);
    transition: background 0.15s ease-in-out;

    // The mdi <i> box is taller than its 24px glyph (line-height), which throws off the centering
    ::v-deep .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
    }
    ::v-deep .mdi {
      display: flex;
      justify-content: center;
      width: 24px;
      height: 24px;
    }

    &:hover {
      background: var(--light);
    }

    &.active {
      background: var(--light);
      color: var(--primary);
    }
  }
}
</style>
